import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed, SearchHistory } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    }

    this.addEventListener('pointerdown', this.#handleRemovePointerDown, { capture: true, signal });
    this.addEventListener('click', this.handleModalClick, { capture: true, signal });

    const urlQuery = new URLSearchParams(window.location.search).get('q');
    if (urlQuery) SearchHistory.addQuery(urlQuery);

    this.#paintSearchHistory();
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #ignoreClickUntil = 0;

  #eventTarget(event) {
    const rawTarget = event.composedPath?.()[0] ?? event.target;
    const target =
      rawTarget instanceof Element ? rawTarget : rawTarget instanceof Node ? rawTarget.parentElement : null;
    return target instanceof HTMLElement ? target : null;
  }

  #handleRemovePointerDown = (event) => {
    if (event.pointerType && event.button !== 0) return;
    const target = this.#eventTarget(event);
    const removeButton = target?.closest('[data-remove-query], [data-remove-viewed]');
    if (!(removeButton instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#ignoreClickUntil = performance.now() + 500;

    if (removeButton.hasAttribute('data-remove-query')) {
      this.#removeSearchQuery(removeButton);
      return;
    }

    this.#removeRecentlyViewedItem(removeButton.dataset.removeViewed || '');
  };

  handleModalClick = (event) => {
    if (performance.now() < this.#ignoreClickUntil) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const target = this.#eventTarget(event);
    if (!target) return;
    const removeQueryButton = target.closest('[data-remove-query]');
    const removeViewedButton = target.closest('[data-remove-viewed]');
    const historyQueryButton = target.closest('[data-search-query]');

    if (removeQueryButton instanceof HTMLElement) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#removeSearchQuery(removeQueryButton);
      return;
    }

    if (removeViewedButton instanceof HTMLElement) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#removeRecentlyViewedItem(removeViewedButton.dataset.removeViewed || '');
      return;
    }

    if (historyQueryButton instanceof HTMLElement) {
      event.preventDefault();
      const query = historyQueryButton.dataset.searchQuery || '';
      if (!query) return;
      this.refs.searchInput.value = query;
      this.#showResetButton();
      SearchHistory.addQuery(query);
      this.#getSearchResults(query);
      return;
    }

    const resultLink = target.closest('a');
    if (resultLink && this.refs.searchInput.value.trim()) {
      this.#commitSearchQuery();
    }

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    this.#paintSearchHistory();
    if (!this.querySelector('.recently-viewed-wrapper') && RecentlyViewed.getProducts().length > 0) {
      this.resetSearch(false);
    }
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          this.#commitSearchQuery();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#commitSearchQuery();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          this.#commitSearchQuery();
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Persist the current query, then continue with the native search submit.
   * @param {SubmitEvent} event
   */
  handleSearchSubmit = (event) => {
    const term = this.refs.searchInput.value.trim();
    if (!term) {
      event.preventDefault();
      return;
    }
    this.#commitSearchQuery(term);
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
        this.#paintSearchHistory();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce(() => {
    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 300);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;
        if (this.refs.searchInput.value.trim() !== searchTerm) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        this.#syncSearchHistoryVisibility();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(viewedProducts.length));
    url.searchParams.set('resources[limit_scope]', 'each');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (recentlyViewedMarkup) {
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
        const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');

        if (recentlyViewedProductsHtml && collectionElement) {
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) {
              child.setAttribute('ref', 'recentlyViewedWrapper');
            }
          }

          collectionElement.replaceChildren(...recentlyViewedProductsHtml.children);
        }
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#syncSearchHistoryVisibility();
    this.#resetScrollPositions();
  };

  /**
   * Persist a finished query only — never mid-typing prefixes.
   * @param {string} [value]
   */
  #commitSearchQuery = (value = this.refs.searchInput?.value || '') => {
    SearchHistory.addQuery(value);
  };

  /**
   * @param {HTMLElement} removeButton
   */
  #removeSearchQuery(removeButton) {
    SearchHistory.removeQuery(removeButton.dataset.removeQuery || '');
    removeButton.closest('.search-history__item')?.remove();

    const history = this.querySelector('[data-search-history]');
    const remaining = this.querySelectorAll('[data-search-history-list] .search-history__item');
    if (history instanceof HTMLElement && remaining.length === 0) {
      history.hidden = true;
    }
    this.#syncSearchHistoryVisibility();
  }

  /**
   * @param {string} productId
   */
  #removeRecentlyViewedItem(productId) {
    if (!productId) return;

    RecentlyViewed.removeProduct(productId);
    const card = this.querySelector(`[data-remove-viewed="${CSS.escape(productId)}"]`)?.closest('li');
    card?.remove();

    const remaining = this.querySelectorAll('[data-remove-viewed]');
    if (remaining.length === 0) {
      this.querySelector('.recently-viewed-wrapper')?.remove();
    }

    this.#syncSearchHistoryVisibility();
  }

  #syncSearchHistoryVisibility() {
    const history = this.querySelector('[data-search-history]');
    if (!(history instanceof HTMLElement)) return;

    const searching = Boolean(this.querySelector('[data-search-results]'));
    const hasQueries = Boolean(this.querySelector('[data-search-history-list] .search-history__item'));
    history.hidden = searching || !hasQueries;

    const hint = this.querySelector('[data-search-empty-hint]');
    const hasViewed = Boolean(this.querySelector('.recently-viewed-wrapper .predictive-search-results__card'));
    if (hint instanceof HTMLElement) {
      hint.hidden = searching || hasQueries || hasViewed;
    }
  }

  #paintSearchHistory() {
    const root = this;
    const history = root.querySelector('[data-search-history]');
    const list = root.querySelector('[data-search-history-list]');
    const hint = root.querySelector('[data-search-empty-hint]');
    if (!(history instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    const queries = SearchHistory.getQueries();
    const removeLabel = history.dataset.removeLabel || 'Remove';
    const searching = Boolean(root.querySelector('[data-search-results]'));
    list.replaceChildren();
    history.hidden = searching || queries.length === 0;

    for (const query of queries) {
      const item = document.createElement('li');
      item.className = 'search-history__item';

      const queryButton = document.createElement('button');
      queryButton.type = 'button';
      queryButton.className = 'search-history__query';
      queryButton.dataset.searchQuery = query;
      queryButton.innerHTML =
        '<span class="search-history__query-icon" aria-hidden="true"></span><span class="search-history__query-text"></span>';
      const text = queryButton.querySelector('.search-history__query-text');
      if (text) text.textContent = query;

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'search-history__remove';
      removeButton.dataset.removeQuery = query;
      removeButton.setAttribute('aria-label', `${removeLabel} ${query}`);
      removeButton.innerHTML =
        '<svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M12 2L2 12M12 12L2 2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
      removeButton.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#ignoreClickUntil = performance.now() + 500;
        this.#removeSearchQuery(removeButton);
      });

      item.append(queryButton, removeButton);
      list.append(item);
    }

    const hasViewed = Boolean(root.querySelector('.recently-viewed-wrapper .predictive-search-results__card'));
    if (hint instanceof HTMLElement) {
      hint.hidden = searching || queries.length > 0 || hasViewed;
    }
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
